import { useState, useEffect, useRef } from 'react';
import { useSelector } from "react-redux";
import { useParams } from 'react-router-dom';
import { toast } from "react-toastify";
import { Modal } from '@mui/material';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import Multiselect from 'multiselect-react-dropdown';
import config from "../../config";
import "bootstrap-icons/font/bootstrap-icons.css";

const Communication = (props) => {
  const { leadFieldPermissions } = props;
  const { id } = useParams();

  // Communication add permission
  const hasAddPermission = !!leadFieldPermissions?.communication?.add;

  const userInfos = useSelector((state) => state.userInfo);
  const useremail = userInfos?.email;
  const userId = userInfos?.id;

  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [messageBody, setMessageBody] = useState('');
  const [mailSubject, setMailSubject] = useState('');
  const [communicationHistory, setCommunicationHistory] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [staticRecipients, setStaticRecipients] = useState([]);
  const [openMailModal, setOpenMailModal] = useState(false);
  const [selectedMailBody, setSelectedMailBody] = useState("");
  const [selectedMailDocuments, setSelectedMailDocuments] = useState("");
  const [previewImage, setPreviewImage] = useState("");
  const [preloaderStatus, setPreloaderStatus] = useState(false);
  const [activeTab] = useState("communication");

  const getUserEmails = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}getuseremail`);
      const result = await response.json();
      if (result.status) {
        const users = result.data.map(item => ({
          label: item.name,
          value: item.email
        }));
        setStaticRecipients(users);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getUserEmails();
  }, []);

  useEffect(() => {
    const resizeHandler = (e) => {
      if (e.message?.includes("ResizeObserver")) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
      return null;
    };
    window.addEventListener("error", resizeHandler, true);
    return () => window.removeEventListener("error", resizeHandler, true);
  }, []);

  const getCommunicationHistory = async () => {
    try {
      if (!id) return;
      const decodedId = atob(id);
      const response = await fetch(
        `${config.API_BASE_URL}get_communication_history?lead_id=${decodedId}`
      );
      const result = await response.json();
      if (result.success === 1 || result.status === 1) {
        setCommunicationHistory(result.data || []);
      } else {
        setCommunicationHistory([]);
      }
    } catch (error) {
      console.log("Communication History Error =>", error);
      setCommunicationHistory([]);
    }
  };

  useEffect(() => {
    if (!id) return undefined;
    getCommunicationHistory();
    const interval = setInterval(() => {
      getCommunicationHistory();
    }, 5000);
    return () => clearInterval(interval);
  }, [id]);

  const sendCommunicationMail = async () => {
    console.log("Attachments =>", attachments);
    const decodedId = atob(id);
    const formData = new FormData();
    formData.append("lead_id", decodedId);
    formData.append("emails", JSON.stringify(selectedRecipients));
    formData.append("subject", mailSubject);
    formData.append("note", messageBody);
    formData.append("created_by", userId);
    attachments.forEach(file => {
      formData.append("attachments[]", file);
    });

    try {
      setPreloaderStatus(true);
      const response = await fetch(`${config.API_BASE_URL}send_communication_mail`, {
        method: "POST",
        body: formData
      });
      const result = await response.json();
      if (result.success === 1 || result.status === 1) {
        setSelectedRecipients([]);
        setMailSubject("");
        setAttachments([]);
        setTimeout(() => { setMessageBody(""); }, 100);
        await getCommunicationHistory();
        toast.success("Communication sent successfully");
      } else {
        toast.error(result.message || "Failed to send communication");
      }
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong");
    } finally {
      setPreloaderStatus(false);
    }
  };

  const startCamera = async () => {
    setShowCameraModal(true);
    setTimeout(async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      } catch (err) {
        toast.error("Camera access denied!", { position: "top-right" });
        setShowCameraModal(false);
      }
    }, 100);
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(track => track.stop());
    setShowCameraModal(false);
  };

  const capturePhoto = () => {
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext("2d").drawImage(videoRef.current, 0, 0);
    canvas.toBlob((blob) => {
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });
      setAttachments(prev => [...prev, file]);
      stopCamera();
    }, "image/jpeg");
  };

  return (
    <>
      <div className="row g-3 mt-2">
        {/* LEFT: Compose */}
        <div className="col-md-7">
          <div className="card" style={{ border: 'none', borderRadius: '14px', boxShadow: '0 2px 12px rgba(0,0,0,.06)', height: '100%' }}>
            <div className="card-body p-4">
              <h3 style={{ fontWeight: '700', marginBottom: '20px' }}>Communication</h3>

              {/* TO */}
              <div className="form-group mb-3">
                <label className="form-label fw-semibold mb-2">To</label>
                <div style={{ border: '1px solid #dfe3e8', borderRadius: '10px', padding: '8px', background: '#fff' }}>
                  <Multiselect
                    options={
                      staticRecipients
                        .filter(item => !selectedRecipients.includes(item.value))
                        .map((item) => ({ key: item.value, value: `${item.label} (${item.value})` }))
                    }
                    displayValue="value"
                    selectedValues={
                      staticRecipients
                        .filter(item => selectedRecipients.includes(item.value))
                        .map((item) => ({ key: item.value, value: `${item.label} (${item.value})` }))
                    }
                    onSelect={(selectedItems) => setSelectedRecipients(selectedItems.map(item => item.key))}
                    onRemove={(selectedItems) => setSelectedRecipients(selectedItems.map(item => item.key))}
                    placeholder="Select recipients"
                    style={{
                      chips: { background: '#1e88e5', borderRadius: '6px', color: '#fff', padding: '4px 8px' },
                      searchBox: { border: 'none', minHeight: '42px' }
                    }}
                  />
                </div>
              </div>

              {/* SUBJECT */}
              <div className="mb-3">
                <label className="form-label fw-bold">Subject</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter mail subject..."
                  value={mailSubject}
                  onChange={(e) => setMailSubject(e.target.value)}
                />
              </div>

              {/* FILE + CAMERA BUTTONS */}
              <div style={{ display: "flex", gap: "12px", marginTop: "15px", alignItems: "center" }}>
                <input
                  ref={cameraInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  id="communicationFilePicker"
                  hidden
                  onChange={(e) => {
                     console.log("Selected Files =>", e.target.files);
                    const files = Array.from(e.target.files || []);
                    console.log("Selected Files =>", files);
                    if (files.length > 0) setAttachments(prev => [...prev, ...files]);
                    e.target.value = "";
                  }}
                />
                <label
                  htmlFor="communicationFilePicker"
                  style={{ background: "#FFC107", color: "#000", padding: "8px 16px", borderRadius: "4px", fontWeight: "400", fontSize: "12px", cursor: "pointer", height: "35.6px", minWidth: "135px", display: "flex", alignItems: "center", justifyContent: "center", margin: 0, textTransform: "uppercase" }}
                >
                  SELECT PHOTOS
                </label>
                <button
                  type="button"
                  onClick={startCamera}
                  style={{ background: "#198754", color: "#fff", border: "none", padding: "8px 16px", borderRadius: "4px", fontWeight: "400", fontSize: "12px", cursor: "pointer", height: "35.6px", minWidth: "135px", textTransform: "uppercase" }}
                >
                  OPEN CAMERA
                </button>
              </div>

              {/* Attachments preview */}
              {attachments.length > 0 && (
                <div style={{ marginTop: "10px", fontSize: "12px", color: "#555" }}>
                  {attachments.length} file(s) selected:&nbsp;
                  {attachments.map((f, i) => (
                    <span key={i} style={{ marginRight: "8px", background: "#f0f0f0", padding: "2px 6px", borderRadius: "4px" }}>
                      {f.name}
                      <button type="button" onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))} style={{ background: "none", border: "none", cursor: "pointer", color: "red", marginLeft: "4px" }}>✕</button>
                    </span>
                  ))}
                </div>
              )}

              {/* MESSAGE (CKEditor) */}
              <div className="mb-3 mt-3">
                <label className="form-label fw-bold">Message</label>
                <CKEditor
                  key={activeTab}
                  editor={ClassicEditor}
                  data={messageBody || ""}
                  onReady={(editor) => {
                    editor.editing.view.change((writer) => {
                      writer.setStyle("min-height", "150px", editor.editing.view.document.getRoot());
                    });
                  }}
                  config={{ placeholder: "Write communication..." }}
                  onChange={(event, editor) => { setMessageBody(editor.getData()); }}
                />
              </div>

              {hasAddPermission && (
              <button
                type="button"
                className="btn btn-success"
                onClick={sendCommunicationMail}
                disabled={preloaderStatus}
              >
                {preloaderStatus ? (
                  <><span className="spinner-border spinner-border-sm me-2" />Sending...</>
                ) : "SUBMIT"}
              </button>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: Activity */}
        <div className="col-md-5">
          <div style={{ padding: "0 4px", maxHeight: "700px", overflowY: "auto", overflowX: "hidden", paddingRight: "6px" }}>
            <h3 style={{ fontWeight: "500", fontSize: "18px", marginBottom: "14px", position: "sticky", top: 0, background: "#fff", zIndex: 10, paddingBottom: "8px" }}>
              Activity
            </h3>

            {communicationHistory.length > 0 ? (() => {
              const threads = [];
              const threadMap = {};
              communicationHistory.forEach((item) => {
                const sender = item.created_by_email?.trim().toLowerCase() || "";
                const recipients = item.emails ? JSON.parse(item.emails).map(e => e.trim().toLowerCase()).sort() : [];
                const participants = [sender, ...recipients].filter((v, i, a) => a.indexOf(v) === i).sort();
                const pairKey = participants.join("|");
                const baseSubject = (item.subject || "").replace(/^(Re:\s*)*/i, "").trim();
                if (!threadMap[pairKey]) {
                  threadMap[pairKey] = [];
                  threads.push({ baseSubject, pairKey, participants, messages: threadMap[pairKey] });
                }
                threadMap[pairKey].push(item);
              });

              return threads.map((thread, tIdx) => (
                <div key={tIdx} style={{ marginBottom: "16px", border: "1px solid #e5e7eb", borderRadius: "14px", overflow: "hidden", background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,.05)" }}>
                  <div style={{ padding: "10px 14px", background: "#f3f4f6", borderBottom: "1px solid #e5e7eb", fontSize: "13px", fontWeight: "600", color: "#374151", display: "flex", alignItems: "center", gap: "8px" }}>
                    <i className="bi bi-envelope" style={{ color: "#3b82f6", fontSize: "14px" }} />
                    {thread.baseSubject || "Communication"}
                    <span style={{ marginLeft: "auto", fontSize: "11px", fontWeight: "400", color: "#9ca3af" }}>
                      {thread.messages.length} {thread.messages.length === 1 ? "message" : "messages"}
                    </span>
                  </div>
                  <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: "8px" }}>
                    {thread.messages.map((item, mIdx) => {
                      const isMine = item.created_by_email?.trim().toLowerCase() === useremail?.trim().toLowerCase();
                      const isReply = !isMine;
                      return (
                        <div key={mIdx} style={{ borderLeft: isReply ? "3px solid #dbeafe" : "none", paddingLeft: isReply ? "12px" : "0", position: "relative" }}>
                          {isReply && (<div style={{ position: "absolute", left: "-10px", top: "14px", width: "8px", height: "8px", borderRadius: "50%", background: "#3b82f6" }} />)}
                          <div style={{ background: isMine ? "#eff6ff" : "#fafafa", border: `1px solid ${isMine ? "#bfdbfe" : "#e5e7eb"}`, borderRadius: "10px", padding: "10px 12px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                              <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: isMine ? "#dbeafe" : "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: "12px", fontWeight: "700", color: isMine ? "#1d4ed8" : "#6b7280" }}>
                                {(item.created_by_email || "?")[0].toUpperCase()}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ fontSize: "12px", fontWeight: "600", color: "#111827", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {item.created_by_email || "-"}
                                  {isMine && <span style={{ fontSize: "10px", fontWeight: "400", color: "#3b82f6", marginLeft: "6px" }}>You</span>}
                                </p>
                                <p style={{ fontSize: "10px", color: "#9ca3af", margin: 0 }}>
                                  To: {item.emails ? JSON.parse(item.emails).join(", ") : "-"}
                                </p>
                              </div>
                              <p style={{ fontSize: "10px", color: "#9ca3af", margin: 0, flexShrink: 0 }}>
                                {new Date(item.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                              </p>
                            </div>
                            <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setSelectedMailBody(item.note || "-"); setSelectedMailDocuments(item.documents || "[]"); setOpenMailModal(true); }}
                                style={{ fontSize: "11px", padding: "3px 10px", borderRadius: "6px", border: "0.5px solid #d1d5db", background: "#f3f4f6", color: "#374151", cursor: "pointer" }}
                              >
                                View mail body
                              </button>
                              {!isMine && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setSelectedRecipients([item.created_by_email]); setMailSubject(`Re: ${thread.baseSubject}`); }}
                                  style={{ fontSize: "11px", padding: "3px 10px", borderRadius: "6px", border: "0.5px solid #93c5fd", background: "#eff6ff", color: "#1d4ed8", cursor: "pointer" }}
                                >
                                  ↩ Reply
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ));
            })() : (
              <p style={{ textAlign: "center", color: "#9ca3af", padding: "20px 0", fontSize: "13px" }}>No activity yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Camera Modal */}
      {showCameraModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 99999, padding: "20px" }}>
          <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", maxWidth: "480px", borderRadius: "12px", background: "#000", maxHeight: "60vh" }}>
            <track kind="captions" />
          </video>
          <div style={{ display: "flex", gap: "16px", marginTop: "20px" }}>
            <button className="btn btn-success btn-lg" type="button" onClick={capturePhoto}>📸 Capture</button>
            <button className="btn btn-danger btn-lg" type="button" onClick={stopCamera}>✕ Cancel</button>
          </div>
        </div>
      )}

      {/* Mail Body Modal */}
      <Modal open={openMailModal} onClose={() => setOpenMailModal(false)}>
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", backdropFilter: "blur(4px)", display: "flex", justifyContent: "center", alignItems: "center", padding: "20px", zIndex: 9999 }}>
          <div style={{ width: "700px", maxWidth: "95%", maxHeight: "85vh", background: "#fff", borderRadius: "20px", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,.18)" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fafbfd" }}>
              <div>
                <div style={{ fontSize: "22px", fontWeight: "700", color: "#111" }}>📩 Mail Body</div>
                <div style={{ fontSize: "13px", color: "#777", marginTop: "3px" }}>Communication Content</div>
              </div>
              <button onClick={() => setOpenMailModal(false)} style={{ border: "none", width: "36px", height: "36px", borderRadius: "50%", background: "#f3f4f6", fontSize: "18px", cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ padding: "24px", maxHeight: "55vh", overflowY: "auto" }}>
              <div style={{ background: "#f8fafc", padding: "20px", borderRadius: "14px", border: "1px solid #edf1f7", fontSize: "15px", lineHeight: "1.8", color: "#333" }}
                dangerouslySetInnerHTML={{ __html: selectedMailBody || "<p>-</p>" }}
              />
              {JSON.parse(selectedMailDocuments || "[]").length > 0 && (
                <div style={{ marginTop: "25px" }}>
                  <h6 style={{ fontWeight: 700, marginBottom: 18, fontSize: 18 }}>📎 Attachments</h6>
                  <div style={{ maxHeight: "250px", overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: "12px", padding: "15px", background: "#fafafa" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "15px" }}>
                      {JSON.parse(selectedMailDocuments || "[]").map((file, index) => {
                        const fileUrl = `${config.API_BASE_URL}uploads/communication/${file}`;
                        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(file);
                        return isImage ? (
                          <div key={index} style={{ width: "150px", border: "1px solid #e5e7eb", borderRadius: "12px", overflow: "hidden", background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,.08)" }}>
                            <button type="button" onClick={() => setPreviewImage(fileUrl)} style={{ border: "none", padding: 0, background: "transparent", cursor: "pointer", width: "100%" }}>
                              <img src={fileUrl} alt="Attachment" style={{ width: "100%", height: "120px", objectFit: "cover" }} />
                            </button>
                            <div style={{ padding: "10px", textAlign: "center" }}>
                              <a href={fileUrl} download className="btn btn-success btn-sm" style={{ width: "100%" }}>View</a>
                            </div>
                          </div>
                        ) : (
                          <div key={index} style={{ width: "220px", padding: "15px", border: "1px solid #ddd", borderRadius: "12px", background: "#fff" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                              <i className="bi bi-file-earmark-text" style={{ fontSize: "30px", color: "#2563eb" }} />
                              <div style={{ fontSize: "13px", wordBreak: "break-word" }}>{file}</div>
                            </div>
                            <a href={fileUrl} download className="btn btn-primary btn-sm" style={{ width: "100%" }}>Download</a>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div style={{ padding: "16px 24px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end" }}>
              <button className="btn btn-dark" style={{ borderRadius: "10px", padding: "8px 22px", fontWeight: "600" }} onClick={() => setOpenMailModal(false)}>Close</button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Image Preview Modal */}
      <Modal open={!!previewImage} onClose={() => setPreviewImage("")}>
        <div role="button" tabIndex={0} onClick={() => setPreviewImage("")} onKeyDown={(e) => { if (e.key === "Escape" || e.key === "Enter") setPreviewImage(""); }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.85)", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <img src={previewImage} alt="Preview" role="presentation" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} style={{ maxWidth: "90%", maxHeight: "90%", borderRadius: "10px" }} />
          <a href={previewImage} download className="btn btn-success" style={{ position: "absolute", top: 20, right: 70 }}>View</a>
          <button onClick={() => setPreviewImage("")} style={{ position: "absolute", top: 20, right: 20, width: "40px", height: "40px", border: "none", borderRadius: "50%", background: "#fff", cursor: "pointer", fontSize: "18px" }}>✕</button>
        </div>
      </Modal>
    </>
  );
};

export default Communication;